import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { categories } from "@/mocks/categories";

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const [catOpen, setCatOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const catRef = useRef<HTMLDivElement>(null);
  const langRef = useRef<HTMLDivElement>(null);
  const mobileLangRef = useRef<HTMLDivElement>(null);
  const mobileRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (catRef.current && !catRef.current.contains(e.target as Node)) setCatOpen(false);
      if (langRef.current && !langRef.current.contains(e.target as Node)) setLangOpen(false);
      if (mobileLangRef.current && !mobileLangRef.current.contains(e.target as Node)) setLangOpen(false);
      if (mobileRef.current && !mobileRef.current.contains(e.target as Node)) setMobileOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
    setLangOpen(false);
    setMobileOpen(false);
  };

  const navLinkClass = "text-sm font-medium text-foreground-800 hover:text-foreground-950 transition-colors whitespace-nowrap cursor-pointer";

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-lg border-b border-background-200/80 shadow-sm">
      <nav className="max-w-7xl mx-auto px-4 md:px-6 flex items-center justify-between h-16">
        {/* Logo */}
        <a href="/" className="text-lg font-bold text-foreground-950 tracking-tight whitespace-nowrap">
          {t("nav_brand")}
        </a>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-5">
          <a href="/" className={navLinkClass}>{t("nav_home")}</a>

          {/* Categories Dropdown */}
          <div ref={catRef} className="relative">
            <button
              onClick={() => setCatOpen(!catOpen)}
              className="flex items-center gap-1 text-sm font-medium text-foreground-800 hover:text-foreground-950 transition-colors whitespace-nowrap cursor-pointer"
            >
              {t("nav_categories")}
              <i className={`ri-arrow-down-s-line transition-transform ${catOpen ? "rotate-180" : ""}`} />
            </button>
            {catOpen && (
              <div className="absolute top-full right-0 mt-2 w-64 bg-white rounded-xl border border-background-200 shadow-xl py-2 max-h-[70vh] overflow-y-auto">
                <div className="px-4 py-2 border-b border-background-100">
                  <span className="text-xs font-semibold text-foreground-500 uppercase tracking-wider">{t("nav_categories")}</span>
                </div>
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-2 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-50 hover:text-primary-600 transition-colors"
                    onClick={() => setCatOpen(false)}
                  >
                    <i className="ri-arrow-right-up-line text-foreground-400 text-xs" />
                    {cat.name}
                  </a>
                ))}
              </div>
            )}
          </div>

          <a href="/article/cssbuy-spreadsheet-guide" className={navLinkClass}>{t("nav_article")}</a>

          {/* Language Dropdown */}
          <div ref={langRef} className="relative">
            <button
              onClick={() => setLangOpen(!langOpen)}
              className="flex items-center gap-1 text-sm font-medium text-foreground-800 hover:text-foreground-950 transition-colors whitespace-nowrap cursor-pointer"
            >
              {i18n.language === "pt" ? "PT" : i18n.language === "de" ? "DE" : "EN"}
              <i className={`ri-arrow-down-s-line transition-transform ${langOpen ? "rotate-180" : ""}`} />
            </button>
            {langOpen && (
              <div className="absolute top-full right-0 mt-2 w-32 bg-white rounded-xl border border-background-200 shadow-xl py-2">
                <button onClick={() => changeLanguage("en")} className="block w-full text-left px-4 py-2 text-sm text-foreground-700 hover:bg-background-50 transition-colors cursor-pointer">English</button>
                <button onClick={() => changeLanguage("pt")} className="block w-full text-left px-4 py-2 text-sm text-foreground-700 hover:bg-background-50 transition-colors cursor-pointer">Português</button>
                <button onClick={() => changeLanguage("de")} className="block w-full text-left px-4 py-2 text-sm text-foreground-700 hover:bg-background-50 transition-colors cursor-pointer">Deutsch</button>
              </div>
            )}
          </div>

          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center gap-2 bg-primary-500 text-white text-sm font-medium px-4 py-2 rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            {t("cta_main_site")}
          </a>
        </div>

        {/* Mobile Menu Button */}
        <div className="flex md:hidden items-center gap-3">
          <div ref={mobileLangRef} className="relative">
            <button
              onClick={() => setLangOpen(!langOpen)}
              className="text-sm font-medium text-foreground-800 cursor-pointer"
            >
              {i18n.language === "pt" ? "PT" : i18n.language === "de" ? "DE" : "EN"}
            </button>
            {langOpen && (
              <div className="absolute top-full right-0 mt-2 w-28 bg-white rounded-xl border border-background-200 shadow-xl py-2 z-50">
                <button onClick={() => changeLanguage("en")} className="block w-full text-left px-4 py-2 text-sm text-foreground-700 hover:bg-background-50 transition-colors cursor-pointer">English</button>
                <button onClick={() => changeLanguage("pt")} className="block w-full text-left px-4 py-2 text-sm text-foreground-700 hover:bg-background-50 transition-colors cursor-pointer">Português</button>
                <button onClick={() => changeLanguage("de")} className="block w-full text-left px-4 py-2 text-sm text-foreground-700 hover:bg-background-50 transition-colors cursor-pointer">Deutsch</button>
              </div>
            )}
          </div>
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="w-10 h-10 flex items-center justify-center rounded-lg bg-background-100 text-foreground-800 hover:bg-background-200 transition-colors cursor-pointer"
          >
            <i className={`ri-${mobileOpen ? "close" : "menu"}-line text-xl`} />
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div ref={mobileRef} className="md:hidden bg-white border-t border-background-200 shadow-lg max-h-[80vh] overflow-y-auto">
          <div className="px-4 py-4 space-y-1">
            <a href="/" onClick={() => setMobileOpen(false)} className="flex items-center gap-2 px-4 py-3 rounded-lg text-sm font-medium text-foreground-800 hover:bg-background-50 transition-colors">
              <i className="ri-home-line" /> {t("nav_home")}
            </a>
            <a href="/article/cssbuy-spreadsheet-guide" onClick={() => setMobileOpen(false)} className="flex items-center gap-2 px-4 py-3 rounded-lg text-sm font-medium text-foreground-800 hover:bg-background-50 transition-colors">
              <i className="ri-book-open-line" /> {t("nav_article")}
            </a>

            {/* Mobile Categories */}
            <div className="border-t border-background-100 pt-2 mt-2">
              <p className="px-4 py-2 text-xs font-semibold text-foreground-500 uppercase tracking-wider">{t("nav_categories")}</p>
              <div className="grid grid-cols-2 gap-1">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-1.5 px-4 py-2.5 rounded-lg text-sm text-foreground-700 hover:bg-background-50 hover:text-primary-600 transition-colors"
                    onClick={() => setMobileOpen(false)}
                  >
                    <i className="ri-arrow-right-up-line text-foreground-400 text-xs" />
                    {cat.name}
                  </a>
                ))}
              </div>
            </div>

            <div className="border-t border-background-100 pt-3 mt-2">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                onClick={() => setMobileOpen(false)}
                className="flex items-center justify-center gap-2 bg-primary-500 text-white text-sm font-medium px-4 py-3 rounded-lg hover:bg-primary-600 transition-colors"
              >
                {t("cta_main_site")}
                <i className="ri-external-link-line" />
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}